import React, { useEffect, useState } from "react";
import axios from "axios";
import { Tooltip } from "./tooltips.js";
import { useDateRange } from "./dateRangeContext.js";
const MODULE_NAME = "_51_index_compare_chart";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

function formatPercent(val) {
    if (val === null || val === undefined || isNaN(val)) return "--";
    return `${val > 0 ? "+" : ""}${Number(val).toFixed(2)}%`;
}

function formatDateMMDDYY(dateStr) {
    // Accepts "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SS" and returns "MM/DD/YY"
    if (!dateStr) return dateStr;
    // If already a week label, return as is
    if (dateStr.includes("-W")) return dateStr;
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
        const [, yyyy, mm, dd] = match;
        return `${mm}/${dd}/${yyyy.slice(2)}`;
    }
    return dateStr;
}

function formatWeekLabel(week, date) {
    return `${week} (${formatDateMMDDYY(date)})`;
}

export default function IndexCompareChart({ style }) {
    const { windowDays } = useDateRange(); // Use global date range
    const [chartData, setChartData] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [tooltip, setTooltip] = useState("");
    const [collapsed, setCollapsed] = useState(true);
    const [showTable, setShowTable] = useState(false); // <-- Add this line

    // Always send window_days as a string, and pass "YTD" as-is
    function getWindowDays(val) {
        if (!val) return "90";
        if (typeof val === "string" && val.toUpperCase() === "YTD") return "YTD";
        return String(val);
    }

    useEffect(() => {
        setLoading(true);
        setError("");
        setChartData(null);

        const window_days = getWindowDays(windowDays);

        axios
            .get(`${process.env.REACT_APP_API_URL}/api/index-compare-chart`, {
                params: { window_days },
                withCredentials: true // <-- send cookies/session for flask_login
            })
            .then((res) => {
                setChartData(res.data.chart_data);
                setTooltip(res.data.tooltip || "");
                setError("");
            })
            .catch((err) => {
                setError("Unable to fetch chart data.");
                setChartData(null);
            })
            .finally(() => setLoading(false));
    }, [windowDays]);

    // Prepare data for chart
    let chartRows = [];
    let period = "90d";
    if (windowDays && (String(windowDays).toUpperCase() === "YTD" || Number(windowDays) >= 365)) {
        period = "ytd";
    } else if (windowDays && Number(windowDays) <= 60) {
        period = "60d";
    } else {
        period = "90d";
    }

    if (chartData && chartData[period]) {
        chartRows = chartData[period].map((row) => ({
            name: period === "ytd" ? formatWeekLabel(row.week, row.date) : formatDateMMDDYY(row.date),
            spy_pct: row.spy_pct,
            user_pct: row.user_pct,
            relative_pct: row.relative_pct,
            week: row.week,
            date: row.date,
        }));
    }

    // Calculate outperformance/underperformance summary
    let outperform = 0, underperform = 0, tie = 0;
    chartRows.forEach(row => {
        if (row.relative_pct > 0) outperform++;
        else if (row.relative_pct < 0) underperform++;
        else tie++;
    });

    // Chart.js data and options
    const chartLabels = chartRows.map((row) => row.name);
    const spyData = chartRows.map((row) => row.spy_pct);
    const userData = chartRows.map((row) => row.user_pct);
    const relData = chartRows.map((row) => row.relative_pct);

    const data = {
        labels: chartLabels,
        datasets: [
            {
                label: "SPY %",
                data: spyData,
                borderColor: "#8884d8",
                backgroundColor: "rgba(136,132,216,0.15)",
                tension: 0.3,
                fill: false,
                yAxisID: "y",
            },
            {
                label: "Your %",
                data: userData,
                borderColor: "#00b894",
                backgroundColor: "rgba(0,184,148,0.15)",
                tension: 0.3,
                fill: false,
                yAxisID: "y",
            },
            {
                label: "% vs S&P",
                data: relData,
                borderColor: "#e63946",
                backgroundColor: "rgba(230,57,70,0.15)",
                tension: 0.3,
                fill: false,
                yAxisID: "y",
            },
        ],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { display: true },
            title: { display: true, text: "Index Comparison Chart" },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return `${context.dataset.label}: ${formatPercent(context.parsed.y)}`;
                    },
                },
            },
        },
        scales: {
            y: {
                title: { display: true, text: "Percent Change" },
                ticks: {
                    callback: function (value) {
                        return formatPercent(value);
                    },
                    color: "#fff",
                },
                grid: { color: "#444" },
            },
            x: {
                ticks: { color: "#fff" },
                grid: { color: "#444" },
            },
        },
    };

    const renderTable = () => {
        if (!chartData || !chartData[period] || chartData[period].length === 0) {
            return (
                <div style={{ color: "#bbb", marginTop: 16 }}>
                    No data available for this period.
                </div>
            );
        }
        const rows = chartData[period];
        return (
            <table
                style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    background: "#232946",
                    marginTop: 12,
                }}
            >
                <thead>
                    <tr style={{ background: "#333", color: "#fff" }}>
                        {period === "ytd" ? (
                            <th style={{ padding: "8px 4px", textAlign: "left" }}>Week</th>
                        ) : null}
                        <th style={{ padding: "8px 4px", textAlign: "left" }}>Date</th>
                        <th style={{ textAlign: "left" }}>SPY %</th>
                        <th style={{ textAlign: "left" }}>Your %</th>
                        <th style={{ textAlign: "left" }}>% vs S&amp;P</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, idx) => (
                        <tr
                            key={idx}
                            style={{
                                background: idx % 2 === 0 ? "#232946" : "#1a1a2e",
                            }}
                        >
                            {period === "ytd" ? (
                                <td>{formatWeekLabel(row.week, row.date)}</td>
                            ) : null}
                            <td>{formatDateMMDDYY(row.date)}</td>
                            <td>{formatPercent(row.spy_pct)}</td>
                            <td>{formatPercent(row.user_pct)}</td>
                            <td
                                style={{
                                    color:
                                        row.relative_pct > 0
                                            ? "#00b894"
                                            : row.relative_pct < 0
                                            ? "#e63946"
                                            : "#fff",
                                }}
                            >
                                {formatPercent(row.relative_pct)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <div
            className={`collapse-toggle-container${collapsed ? " collapsed" : ""}`}
        >
            <h4 
                className="collapse-toggle-headline"
                onClick={() => setCollapsed(!collapsed)}
            >
                📈 Index Comparison Chart
                <Tooltip text={tooltip} />
            </h4>
            {!collapsed && (
                <>
                    <div style={{ width: "100%", height: 320 }}>
                        {loading ? (
                            <div>Loading...</div>
                        ) : error ? (
                            <div style={{ color: "#e63946" }}>{error}</div>
                        ) : (
                            <Chart type="line" data={data} options={options} />
                        )}
                    </div>
                    {/* Summary Section */}
                    <div style={{ marginTop: 18, fontSize: 16, color: "#fff", background: "#1a1a2e", borderRadius: 6, padding: "12px 18px", display: "inline-block" }}>
                        <span style={{ color: "#00b894", fontWeight: 600 }}>
                            Outperformed: {outperform}
                        </span>
                        <span style={{ margin: "0 18px", color: "#e63946", fontWeight: 600 }}>
                            Underperformed: {underperform}
                        </span>
                        <span style={{ color: "#bbb", fontWeight: 500 }}>
                            Tied: {tie}
                        </span>
                    </div>
                    {/* Table Toggle Button */}
                    <div style={{ marginTop: 16 }}>
                        <button
                            onClick={() => setShowTable((prev) => !prev)}
                            style={{
                                background: "#232946",
                                color: "#fff",
                                border: "1px solid #444",
                                borderRadius: 4,
                                padding: "6px 16px",
                                cursor: "pointer",
                                fontSize: 15,
                            }}
                        >
                            {showTable ? "Hide Table" : "Show Table"}
                        </button>
                    </div>
                    {/* Conditionally render the table */}
                    {showTable && renderTable()}
                </>
            )}
        </div>
    );
}